/**
 * Underline tab bar.
 *
 * Used by What's New to switch approval buckets, with the count of each bucket as a badge.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';

export interface TabItem {
  id: string;
  label: string;
  icon?: IGLIcon['variant'];
  /** Count shown next to the label. `0` renders, `undefined` does not. */
  badge?: number;
}

const listStyle = css`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--color-border);
`;

const tabStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  white-space: nowrap;
  transition: color var(--duration-fast) var(--ease-standard);
`;

const tabActiveStyle = css`
  border-bottom-color: var(--color-primary);
  color: var(--color-primary);
`;

const badgeStyle = css`
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-muted);
  background: var(--color-surface-sunken);
  border-radius: var(--radius-round);
  padding: 1px 7px;
`;

const badgeActiveStyle = css`
  color: var(--color-primary);
  background: var(--color-primary-subtle);
`;

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
}

export const Tabs = ({ items, activeId, onChange }: TabsProps): JSX.Element => (
  <div role='tablist' className={listStyle}>
    {items.map(item => {
      const isActive = item.id === activeId;

      return (
        <button
          key={item.id}
          id={`tab-${item.id}`}
          type='button'
          role='tab'
          aria-selected={isActive}
          className={`${tabStyle} ${isActive ? tabActiveStyle : ''}`}
          onClick={() => {
            onChange(item.id);
          }}
        >
          {item.icon ? <GLIcon variant={item.icon} size={18} fill='currentColor' /> : null}
          {item.label}
          {item.badge !== undefined ? (
            <span className={`${badgeStyle} ${isActive ? badgeActiveStyle : ''}`}>
              {item.badge}
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
);
